// date format as DD.MM.YY
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * Resolve the zone these helpers format in. `dayjs.tz.guess()` reads the
 * ambient zone, which is the Node process zone (UTC) during SSR and the
 * visitor's zone in the browser — so any date rendered as text through it
 * mismatches on hydration. Callers that render during SSR must pass the
 * request-derived zone (`useLocalisation().timezone`) so both passes agree.
 */
const resolveTimeZone = (timeZone?: string): string =>
    timeZone ?? dayjs.tz.guess();

export function formatDateToDDMMYY(
    date: string | number | Date,
    timeZone?: string,
): string {
    let dayjsDate;

    if (typeof date === 'number') {
        // Handle both millisecond and second timestamps
        dayjsDate = dayjs(date < 10000000000 ? date * 1000 : date);
    } else if (typeof date === 'string' && /^\d+$/.test(date)) {
        // Handle numeric strings as timestamps
        const timestamp = parseInt(date, 10);
        dayjsDate = dayjs(
            timestamp < 10000000000 ? timestamp * 1000 : timestamp,
        );
    } else {
        dayjsDate = dayjs(date);
    }

    const localTimezone = resolveTimeZone(timeZone);
    return dayjsDate.isValid()
        ? dayjs.utc(dayjsDate.valueOf()).tz(localTimezone).format('DD.MM.YY')
        : '';
}

export const formatDateSplit = (
    date: string,
    format?: { time?: string; date?: string; month?: string },
    timeZone?: string,
) => {
    const localTimezone = resolveTimeZone(timeZone);
    const localDate = dayjs.utc(date).tz(localTimezone);

    return {
        time: localDate.format(format?.time ?? 'h:mmA'),
        date: localDate.format(format?.date ?? 'ddd D MMM YYYY'),
        month: localDate.format(format?.month ?? 'MMMM YYYY'),
    };
};

export function formatRelativeTime(
    date: string | number | Date,
    timeZone?: string,
): string {
    // Convert UTC date to local timezone before calculating relative time
    const localTimezone = resolveTimeZone(timeZone);
    return dayjs.utc(date).tz(localTimezone).fromNow();
}
